// src/admin/components/HomePageConfig/HowWeWorkSection.jsx
import React from "react";
import ColorSection from "./ColorSection";
import ColorPicker from "./ColorPicker";
import FontSizeSection from "./FontSizeSection";

export default function HowWeWorkSection({ 
  howWeWork, 
  setHowWeWork,
  howWeWorkColors = {},
  setHowWeWorkColors
}) {
  
  // Provide default values
  const safeHowWeWork = howWeWork || {
    display: true,
    sectionTitle: "HOW WE WORK",
    sectionSubtitle: "At Regency Interior Design Company, we believe that the journey of transforming a space should be as enjoyable as the end result. Here's how we bring your vision to life:",
    backgroundColor: "#111111",
    titleColor: "#d4af37",
    subtitleColor: "rgba(255, 255, 255, 0.8)",
    cardBackground: "#ffffff",
    numberColor: "#d4af37",
    textColor: "#111111",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
    steps: []
  };
  
  const safeHowWeWorkColors = howWeWorkColors || {
    headingColor: "#d4af37",
    subHeadingColor: "rgba(255, 255, 255, 0.8)",
    contentColor: "#111111",
    numberColor: "#d4af37",
    cardBgColor: "#ffffff",
  };

  const handleColorChange = (property, value) => {
    if (setHowWeWorkColors) {
      setHowWeWorkColors(prev => ({
        ...(prev || {}),
        [property]: value === "transparent" ? "transparent" : value
      }));
    }
  };

  const handleHowWeWorkChange = (property, value) => {
    setHowWeWork((prev) => ({
      ...(prev || {}),
      [property]: value
    }));
  };

  const toggleSectionColors = (useSectionColors) => {
    setHowWeWork(prev => ({
      ...(prev || {}),
      useSectionColors
    }));
  };

  const toggleSectionFontSizes = (useSectionFontSizes) => {
    setHowWeWork((prev) => ({
      ...(prev || {}),
      useSectionFontSizes,
    }));
  };

  const handleFontSizeChange = (key, value) => {
    setHowWeWork((prev) => ({
      ...(prev || {}),
      fontSizes: {
        ...(prev?.fontSizes || {}),
        [key]: value,
      },
    }));
  };

  const addStep = () => {
    const newStepNumber = safeHowWeWork.steps.length + 1;
    const newStep = {
      no: newStepNumber.toString().padStart(2, '0'),
      text: ""
    };
    setHowWeWork(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));
  };

  const removeStep = (index) => {
    setHowWeWork(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const updateStep = (index, field, value) => {
    setHowWeWork(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const moveStep = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const newSteps = [...safeHowWeWork.steps];
      [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
      setHowWeWork(prev => ({ ...prev, steps: newSteps }));
    } else if (direction === 'down' && index < safeHowWeWork.steps.length - 1) {
      const newSteps = [...safeHowWeWork.steps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      setHowWeWork(prev => ({ ...prev, steps: newSteps }));
    }
  };

  return (
    <div className="hcfg-card">
      <div className="hcfg-headrow">
        <h3>How We Work Section</h3>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            checked={safeHowWeWork.display}
            onChange={(e) =>
              setHowWeWork((prev) => ({ ...prev, display: e.target.checked }))
            }
            style={{ width: "18px", height: "18px", cursor: "pointer" }}
          />
          Show on Home Page
        </label>
      </div>

      <div
        className="hcfg-row"
        style={{
          opacity: safeHowWeWork.display ? 1 : 0.6,
          pointerEvents: safeHowWeWork.display ? "auto" : "none",
        }}
      >
        <div className="hcfg-col">
          <label style={{ display: "block", marginBottom: "5px" }}>Section Title</label>
          <input
            value={safeHowWeWork.sectionTitle || ""}
            onChange={(e) => handleHowWeWorkChange("sectionTitle", e.target.value)}
            placeholder="HOW WE WORK"
            style={{ width: "100%", padding: "8px", marginBottom: "15px" }}
          />
          
          <label style={{ display: "block", marginBottom: "5px" }}>Section Subtitle</label>
          <textarea
            rows={3}
            value={safeHowWeWork.sectionSubtitle || ""}
            onChange={(e) => handleHowWeWorkChange("sectionSubtitle", e.target.value)}
            placeholder="Describe your process..."
            style={{ width: "100%", padding: "8px", resize: "vertical" }}
          />
        </div>

        <div className="hcfg-col">
          <ColorPicker
            label="Section Background Color"
            value={safeHowWeWork.backgroundColor || "#111111"}
            onChange={(value) => 
              handleHowWeWorkChange("backgroundColor", value === "transparent" ? "transparent" : value)
            }
            description="Background color for entire section"
            allowTransparent={true}
          />
          
          <ColorPicker
            label="Card Background Color"
            value={safeHowWeWork.cardBackground || "#ffffff"}
            onChange={(value) => 
              handleHowWeWorkChange("cardBackground", value === "transparent" ? "transparent" : value)
            }
            description="Background color for each step card"
            allowTransparent={false}
          />
        </div>
      </div>

      {/* Steps Management */}
      <div style={{ marginTop: "20px" }}>
        <FontSizeSection
          title="How We Work"
          sizes={safeHowWeWork.fontSizes || {}}
          onChange={handleFontSizeChange}
          useSectionFontSizes={safeHowWeWork.useSectionFontSizes === true}
          onToggleSectionFontSizes={toggleSectionFontSizes}
          fields={[
            { key: "heading", label: "Section Title Size", placeholder: "e.g., 42px" },
            { key: "subHeading", label: "Section Subtitle Size", placeholder: "e.g., 16px" },
            { key: "stats", label: "Step Number Size", placeholder: "e.g., 48px" },
            { key: "content", label: "Step Text Size", placeholder: "e.g., 16px" },
          ]}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h4>Process Steps ({safeHowWeWork.steps.length})</h4>
          <button
            onClick={addStep}
            style={{
              padding: "8px 16px",
              background: "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            + Add Step
          </button>
        </div>

        {safeHowWeWork.steps.length === 0 ? (
          <div style={{ 
            padding: "20px", 
            textAlign: "center", 
            background: "#f5f5f5", 
            borderRadius: "4px",
            color: "#666"
          }}>
            No steps added yet. Click "Add Step" to create your process flow.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {safeHowWeWork.steps.map((step, index) => (
              <div key={index} style={{ 
                padding: "15px", 
                border: "1px solid #eee", 
                borderRadius: "4px",
                background: "#f9f9f9",
                position: "relative"
              }}>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  marginBottom: "10px" 
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ 
                      fontWeight: "bold", 
                      color: "#333",
                      backgroundColor: "#e0e0e0",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      minWidth: "60px",
                      textAlign: "center"
                    }}>
                      Step {index + 1}
                    </span>
                    <input
                      type="text"
                      value={step.no || ""}
                      onChange={(e) => updateStep(index, "no", e.target.value)}
                      placeholder="01"
                      style={{ 
                        width: "60px", 
                        padding: "6px", 
                        fontSize: "14px",
                        textAlign: "center"
                      }}
                    />
                  </div>
                  
                  <div style={{ display: "flex", gap: "5px" }}>
                    <button
                      onClick={() => moveStep(index, 'up')}
                      disabled={index === 0}
                      style={{
                        padding: "4px 8px",
                        background: index === 0 ? "#ccc" : "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        fontSize: "12px"
                      }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveStep(index, 'down')}
                      disabled={index === safeHowWeWork.steps.length - 1}
                      style={{
                        padding: "4px 8px",
                        background: index === safeHowWeWork.steps.length - 1 ? "#ccc" : "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: index === safeHowWeWork.steps.length - 1 ? "not-allowed" : "pointer",
                        fontSize: "12px"
                      }}
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeStep(index)}
                      style={{
                        padding: "4px 12px",
                        background: "#f44336",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: "12px", display: "block", marginBottom: "5px" }}>Step Description</label>
                  <textarea
                    rows={2}
                    value={step.text || ""}
                    onChange={(e) => updateStep(index, "text", e.target.value)}
                    placeholder="Describe this step..."
                    style={{ 
                      width: "100%", 
                      padding: "8px", 
                      fontSize: "14px", 
                      resize: "vertical",
                      minHeight: "60px"
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Color Customization for How We Work Section */}
      <ColorSection
        title="How We Work"
        colors={safeHowWeWorkColors}
        onColorChange={handleColorChange}
        useSectionColors={safeHowWeWork.useSectionColors !== false}
        onToggleSectionColors={toggleSectionColors}
      />
    </div>
  );
}
